import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactModal from 'react-modal';

import DatabaseService from "../../services/database.service.js";

import styles from "../styles.module.css";
import gridSxStyle from "../../common/GridSxStyles.js";

import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarExport,
    GridToolbarQuickFilter,
    useGridApiRef,
} from '@mui/x-data-grid';
import { CircularProgress, TextField, Typography } from '@mui/material';

import { convertDateToStringMobile, convertDateToMillisecondsString } from '../../common/ConvertTools.js';

ReactModal.setAppElement('#root');

const DeviceModalLogs = ({ isOpen, isAdmin, onRequestClose, deviceProps, isDesktop }) => {
    const [logsValues, setLogsValues] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [searchText, setSearchText] = useState('');

    const isDark = useMemo(() => localStorage.getItem('themeDark'), []);

    const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: 45 };
    const DEFAULT_FILTER_MODEL = { items: [] };
    const storedPaginationModel = localStorage.getItem('deviceLogsPaginationModel');
    const storedFilterModel = localStorage.getItem('deviceLogsFilterModel');

    const initialPaginationModel = storedPaginationModel ? JSON.parse(storedPaginationModel) : DEFAULT_PAGINATION_MODEL;
    const initialFilterModel = storedFilterModel ? JSON.parse(storedFilterModel) : DEFAULT_FILTER_MODEL;

    const [devicesPaginationModel, setDevicesPaginationModel] = useState(initialPaginationModel);
    const [devicesFilterModel, setDevicesFilterModel] = useState(initialFilterModel);

    const [matchingRowIndices, setMatchingRowIndices] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

    const apiRef = useGridApiRef();

    const highlightText = useCallback((text, searchText) => {
        if (!searchText) return text;

        const escapedSearchText = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearchText})`, 'gi');
        const parts = text.split(regex);

        return (
            <>
                {parts.map((part, index) =>
                    regex.test(part) ? (
                        <span key={index} style={{ backgroundColor: 'yellow' }}>
                            {part}
                        </span>
                    ) : (
                        <span key={index}>{part}</span>
                    )
                )}
            </>
        );
    }, [searchText]);

    const columns = useMemo(
        () => [
            {
                field: 'formattedDate',
                headerName: 'Date',
                flex: 0.3,
                maxWidth: 185,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {highlightText(params.value.toString(), searchText)}
                    </Typography>
                ),
            },
            {
                field: 'text',
                headerName: 'Text',
                flex: 1,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {highlightText(params.value.toString(), searchText)}
                    </Typography>
                ),
            },
        ],
        [highlightText]
    );

    const loadLogs = useCallback(() => {
        setIsLoading(true);
        DatabaseService.getLogsDevice(deviceProps?.mac_custom)
            .then((response) => {
                if (response.data.length > 0) {
                    const formattedData = response.data.map((log, index) => ({
                        id: index,
                        formattedDate: isDesktop ? convertDateToMillisecondsString(log.date) : convertDateToStringMobile(log.date),
                        text: log.text,
                    }));
                    setLogsValues(formattedData);
                } else {
                    setLogsValues([]);
                }
                setDevicesPaginationModel((prevModel) => ({
                    ...prevModel,
                    page: 0,
                }));
                setIsLoading(false);
            })
            .catch((error) => {
                console.log("ModalLogs / getLogsDevice / error", error);
                setIsLoading(false);
            });
    }, [deviceProps?.mac_custom, isDesktop]);

    useEffect(() => {
        if (deviceProps) {
            loadLogs();
        }
    }, [deviceProps, loadLogs]);

    useEffect(() => {
        if (!searchText) {
            setMatchingRowIndices([]);
            setCurrentMatchIndex(0);
            return;
        }

        const matches = logsValues.reduce((acc, row, index) => {
            const rowString = Object.values(row).join(' ').toLowerCase();
            if (rowString.includes(searchText.toLowerCase())) {
                acc.push(index);
            }
            return acc;
        }, []);

        setMatchingRowIndices(matches);
        setCurrentMatchIndex(0);
    }, [searchText, logsValues]);

    const goToNextMatch = useCallback(() => {
        if (matchingRowIndices.length === 0) return;

        const nextIndex = (currentMatchIndex + 1) % matchingRowIndices.length;
        setCurrentMatchIndex(nextIndex);

        const rowIndex = matchingRowIndices[nextIndex];
        const pageSize = devicesPaginationModel.pageSize;
        const pageNumber = Math.floor(rowIndex / pageSize);

        setDevicesPaginationModel((prev) => ({ ...prev, page: pageNumber }));
    }, [currentMatchIndex, matchingRowIndices, devicesPaginationModel.pageSize]);

    useEffect(() => {
        if (matchingRowIndices.length === 0) return;

        const rowIndex = matchingRowIndices[currentMatchIndex];
        const rowId = logsValues[rowIndex]?.id;

        // Delay to ensure DataGrid has rendered
        setTimeout(() => {
            if (apiRef.current && rowIndex !== undefined) {
                apiRef.current.scrollToIndexes({
                    rowIndex,
                });
            }
        }, 100);
    }, [devicesPaginationModel.page, currentMatchIndex, matchingRowIndices, logsValues, apiRef]);

    const handlePaginationModelChange = (paginationModel) => {
        setDevicesPaginationModel(paginationModel);
        localStorage.setItem('deviceLogsPaginationModel', JSON.stringify(paginationModel));
    };

    const handleFilterModelChange = (filterModel) => {
        setDevicesFilterModel(filterModel);
        localStorage.setItem('deviceLogsFilterModel', JSON.stringify(filterModel));
    };

    const closeModal = () => {
        setDevicesFilterModel(DEFAULT_FILTER_MODEL);
        setDevicesPaginationModel(DEFAULT_PAGINATION_MODEL);
        localStorage.setItem('deviceLogsFilterModel', JSON.stringify(DEFAULT_FILTER_MODEL));
        onRequestClose();
    };

    const removeLogs = () => {
        const confirmed = window.confirm('Are you sure you want to delete those data?');
        if (confirmed) {
            DatabaseService.removeLogs(deviceProps?.mac_custom)
                .then((response) => {
                    if (response.status === 200 || response.status === 204) {
                        loadLogs();
                    }
                })
                .catch((error) => {
                    console.log("ModalLogs / removeLogs / error", error);
                });
        }
    };

    const onSearchTextChange = useCallback((e) => {
        setSearchText(e.target.value);
    }, []);

    const TableToolbar = useMemo(() => {
        return function TableToolbar(props) {
            const {
                isDesktop,
                loadLogs,
                onNextMatch,
                hasMatches,
                currentMatchIndex,
                totalMatches,
                searchText,
                onSearchTextChange,
            } = props;

            return (
                <GridToolbarContainer>
                    {isDesktop && (
                        <>
                            <GridToolbarColumnsButton />
                            <GridToolbarFilterButton />
                            <GridToolbarExport />
                        </>
                    )}
                    <button
                        className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiCustomButton"
                        onClick={loadLogs}
                    >
                        <i className="fa-solid fa-arrows-rotate" style={{ color: 'var(--color-foreground)', marginRight: '8px' }}></i>
                        Refresh
                    </button>

                    <GridToolbarQuickFilter debounceMs={500} placeholder="Filter..." />

                    <div
                        className="MuiInputBase-root MuiInput-root MuiInput-underline MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-adornedStart MuiInputBase-adornedEnd MuiInputBase-custom-input-zone"
                        style={{ marginLeft: '3px' }}
                    >
                        <i className="fa-solid fa-magnifying-glass-arrow-right fa-sm" style={{ color: 'var(--color-foreground)', marginRight: '8px', paddingBottom: '4px' }}></i>

                        <input
                            className="MuiInputBase-input MuiInput-input MuiInputBase-inputTypeSearch MuiInputBase-inputAdornedStart MuiInputBase-inputAdornedEnd MuiInputBase-custom-input "
                            size="small"
                            placeholder="Search..."
                            value={searchText}
                            onChange={onSearchTextChange}
                            style={{ marginTop: '-5px' }}
                        />
                    </div>

                    {
                        hasMatches && (
                            <>
                                <button
                                    className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiCustomButton"
                                    onClick={onNextMatch}
                                    style={{ marginLeft: '8px' }}
                                >
                                    Next
                                </button>
                                <span style={{ color: 'var(--color-foreground)' }}>
                                    Match {currentMatchIndex + 1} of {totalMatches}
                                </span>
                            </>
                        )
                    }
                </GridToolbarContainer >
            );
        };
    }, []);

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={closeModal}
            contentLabel="ModalLogs"
            shouldFocusAfterRender={false}
            shouldReturnFocusAfterClose={false}
            style={{
                overlay: {
                    backgroundColor: 'rgba(50, 50, 50, 0.75)',
                    display: 'flex',
                    justifyContent: 'center',
                },
                content: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '95%',
                    width: '100%',
                },
            }}
            className={isDark === "true" ? styles.modalContentDark : styles.modalContentLight}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                    <h5 style={{ fontWeight: 'bold', paddingRight: '5px', whiteSpace: 'nowrap' }}>
                        {deviceProps?.ble_name ? deviceProps?.ble_name : ""}
                    </h5>
                    <h7 style={{ whiteSpace: 'nowrap', paddingBottom: '5px' }}>
                        {deviceProps?.mac_custom}
                    </h7>
                </div>
                <div style={{ display: 'flex', marginLeft: 'auto' }}>
                    <button className="btn btn-outline-primary m-1" onClick={removeLogs} style={{ marginRight: '10px' }}>
                        <i className="fas fa-trash"></i>
                    </button>
                    <button className="btn btn-outline-primary m-1" onClick={closeModal} style={{ marginRight: '10px' }}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
            </div>
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            ) : (
                <div style={{ height: 'calc(100vh - 150px)', width: '100%' }} >
                    <DataGrid
                        apiRef={apiRef}
                        rows={logsValues}
                        columns={columns}
                        checkboxSelection={false}
                        pageSizeOptions={[15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 100]}
                        paginationModel={devicesPaginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        filterModel={devicesFilterModel}
                        onFilterModelChange={handleFilterModelChange}
                        getRowHeight={() => 'auto'}
                        disableDensitySelector
                        getRowClassName={(params) => {
                            const rowId = params.id;
                            const rowIndex = logsValues.findIndex((row) => row.id === rowId);
                            if (rowIndex === matchingRowIndices[currentMatchIndex]) {
                                return 'current-match-row';
                            }
                            return '';
                        }}
                        slots={{ toolbar: TableToolbar }}
                        slotProps={{
                            toolbar: {
                                isDesktop,
                                loadLogs,
                                onNextMatch: goToNextMatch,
                                hasMatches: matchingRowIndices.length > 0,
                                currentMatchIndex,
                                totalMatches: matchingRowIndices.length,
                                searchText,
                                onSearchTextChange,
                            },
                        }}
                        sx={{
                            ...gridSxStyle,
                            '& .current-match-row': {
                                backgroundColor: 'rgba(255, 255, 0, 0.3)',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: 'none',
                                borderLeft: 'none',
                                borderRight: 'none',
                                alignItems: 'flex-start',
                                display: 'flex',
                            },
                        }}
                    />
                </div>
            )}
        </ReactModal>
    );
};

export default DeviceModalLogs;
